/**
 * Mercado público — venta, cambio, subasta, regalo.
 */
import { api } from "./client";

export type ListingType = "sale" | "trade" | "auction" | "gift";
export type ListingStatus = "active" | "sold" | "cancelled" | "expired";

export interface ListingItem {
  sticker_id: number;
  sticker_num: string;
  sticker_name: string;
  team_name: string | null;
  is_main: boolean;
}

export interface Listing {
  id: number;
  seller_id: number;
  sticker_id: number;             // back-compat: id del main
  type: ListingType;
  status: ListingStatus;
  price: number;
  current_bid: number | null;
  condition: string;
  note: string | null;
  photo_url: string | null;
  items: ListingItem[];           // main + extras (siempre incluye el main)
  ends_at: string | null;
  created_at: string;
}

export interface CreateListingPayload {
  sticker_id: number;
  type: ListingType;
  price?: number;
  condition?: string;
  note?: string;
  ends_at?: string;
  extra_sticker_ids?: number[];   // bundle: stickers adicionales aparte del main
}

export async function uploadListingPhoto(listingId: number, file: File): Promise<Listing> {
  const fd = new FormData();
  fd.append("file", file);
  return api<Listing>(`/market-listings/${listingId}/photo`, {
    method: "POST",
    body: fd,
  });
}

export async function fetchListings(opts: { type?: ListingType; sticker_id?: number; limit?: number; offset?: number } = {}): Promise<Listing[]> {
  const params = new URLSearchParams();
  if (opts.type) params.set("type", opts.type);
  if (opts.sticker_id) params.set("sticker_id", String(opts.sticker_id));
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return api<Listing[]>(`/market-listings${qs ? `?${qs}` : ""}`);
}

export async function fetchMyListings(): Promise<Listing[]> {
  return api<Listing[]>("/market-listings/me");
}

export async function createListing(payload: CreateListingPayload): Promise<Listing> {
  return api<Listing>("/market-listings", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function cancelListing(id: number): Promise<void> {
  await api(`/market-listings/${id}`, { method: "DELETE" });
}

export async function markSold(id: number): Promise<void> {
  await api(`/market-listings/${id}/mark-sold`, { method: "POST" });
}
